import { Tree } from "@msuite/ui/dnd";
import type { CalculationFormSchema } from "@msuite/utils/schemas";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CalculationFormItem } from "./calculation-form-item";
import { CalculationFormItemsMenu } from "./calculation-form-items-menu";
import { CalculationFormHandler } from "./shared";

export function CalculationFormItems() {
	/** Hooks */
	const methods = useFormContext<CalculationFormSchema>();
	const services = useWatch({ control: methods.control, name: "services" });

	/** Computed */
	// Rebuild the tree only when the *structure* changes (ids, parentage, order) — not on every
	// value keystroke. Value edits are reflected by the individual row components, which read from
	// the form directly, so re-creating and re-flattening the whole tree on each keystroke is
	// wasted work that re-renders every row.
	const structuralKey = (services ?? [])
		.map(
			(service) =>
				`${service.id}:${service.parentServiceCalculationItemId ?? "root"}:${service.ranking}`,
		)
		.join("|");
	const tree = useMemo(
		() => new CalculationFormHandler(methods.getValues("services")).createTree(),
		[structuralKey],
	);

	// Memoize the tree element so a value-only re-render of this component does not re-render the
	// entire tree subtree (which would re-render every service). Only a structural change produces
	// a new `tree` and therefore a new element; the edited row still updates via its own watch.
	const treeElement = useMemo(
		() => (
			<Tree
				data={tree}
				dataLoader={{
					getItem: (id: string) => tree[id]!,
					getChildren: (id: string) => tree[id]?.children ?? [],
				}}
				modifyExternalItem={(item) => {
					if (!item.id) return item;
					item.organizationServiceId = item.id!;
					item.positions = [{ name: "", ranking: 0 }];
					return item;
				}}
				onChange={(_, record) => {
					const formServices = new CalculationFormHandler(
						methods.getValues("services"),
					).transformRecordToForm(record);
					methods.setValue("services", formServices);
				}}
				render={(item, _, api) => (
					<CalculationFormItem
						item={item}
						api={api}
					/>
				)}
				Menu={(props) => <CalculationFormItemsMenu {...props} />}
			/>
		),
		[tree],
	);

	/** Render */
	return <div className="v-stack relative p-0.5 flex-1 overflow-hidden">{treeElement}</div>;
}
